import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { lomavisCreatorRootState } from "../../../../../pages/lomavisCreator";
import { layerImageThumbnail } from "../../../../../redux/slices/lomavisCreator/lomavisCreator";

type VideoDimensions = {
  width: number;
  height: number;
};

const PostPreview = ({ platform, videoUrl }) => {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const preview_image = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].uploadedImageThumbnail.url
  );
  const editedPreviewImage = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].editedImageThumbnail
  );
  const processingPreviewImage = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].thumbnail_preview_processing
  );
  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };
  const getVideoDimensions = (url: string): Promise<VideoDimensions> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
        });
      };

      video.onerror = () => {
        reject("Error loading video metadata");
      };

      video.src = url;
    });
  };

  const layerImages = async () => {
    console.log(
      "layered image preview urls",
      preview_image,
      editedPreviewImage.media,
      videoUrl
    );
    const startTime = performance.now();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const backgroundImg = await loadImage(preview_image);
    const overlayImg = await loadImage(editedPreviewImage.url);
    const dimensions = await getVideoDimensions(videoUrl);

    // Set canvas dimensions
    canvas.width = dimensions?.width;
    canvas.height = dimensions?.height;

    // Calculate dimensions to cover canvas
    const canvasAspectRatio = canvas.width / canvas.height;
    const backgroundAspectRatio = backgroundImg.width / backgroundImg.height;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (canvasAspectRatio > backgroundAspectRatio) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / backgroundAspectRatio;
      offsetX = 0;
      offsetY = (canvas.height - drawHeight) / 2;
    } else {
      drawWidth = canvas.height * backgroundAspectRatio;
      drawHeight = canvas.height;
      offsetX = (canvas.width - drawWidth) / 2;
      offsetY = 0;
    }

    // Draw the background image with blur
    ctx.filter = "blur(6px)";
    ctx.drawImage(backgroundImg, offsetX, offsetY, drawWidth, drawHeight);

    // Calculate dimensions to fit overlay image within canvas
    const overlayAspectRatio = overlayImg.width / overlayImg.height;
    let overlayWidth, overlayHeight;

    if (overlayAspectRatio > canvasAspectRatio) {
      overlayWidth = canvas.width;
      overlayHeight = canvas.width / overlayAspectRatio;
    } else {
      overlayWidth = canvas.height * overlayAspectRatio;
      overlayHeight = canvas.height;
    }

    // Center overlay image
    const overlayX = (canvas.width - overlayWidth) / 2;
    const overlayY = (canvas.height - overlayHeight) / 2;

    // Draw the overlay image on top
    ctx.filter = "none";
    ctx.drawImage(overlayImg, overlayX, overlayY, overlayWidth, overlayHeight);

    // Convert the canvas to a data URL
    // const dataURL = canvas.toDataURL("image/png");

    canvas.toBlob((blob) => {
      const file = new File([blob], "image.jpg", { type: "image/jpeg" });
      dispatch(
        layerImageThumbnail({
          file: file,
          platform: platform,
        })
      );
    }, "image/jpeg");

    const endTime = performance.now();
    const executionTime = endTime - startTime;
    console.log(`canvas execution time: ${executionTime.toFixed(2)} ms`);

    // Use the data URL as the source for an image element or download it
    const imgElement = document.getElementById("resultImage");
    // imgElement.src = dataURL;
  };

  useEffect(() => {
    layerImages();
  }, [editedPreviewImage]);

  return (
    <div className="w-100 h-100 d-flex justify-content-center align-items-center ">
      <div
        style={{ width: "auto", maxWidth: "80%", overflow: "hidden" }}
        className="border border-top-0 rounded-3 "
      >
        {" "}
        <div className="w-100 p-4 rounded-3 d-flex flex-column gap-3 border-bottom border-3 mx-auto">
          <div className="w-100 d-flex gap-3">
            <div
              style={{ width: "35px", aspectRatio: 1, maxWidth: "20%" }}
              className="rounded-circle border-bottom border-2 bg-secondary bg-opcity-75"
            ></div>
            <div className="w-50 pe-3 d-flex flex-column gap-2 ">
              <div
                style={{ height: "35%", maxHeight: "16px" }}
                className="w-100 bg-secondary bg-opcity-75 border-bottom border-2 rounded-pill"
              ></div>
              <div
                style={{ height: "35%", maxHeight: "16px" }}
                className="w-100 bg-secondary bg-opcity-75 border-bottom border-2 rounded-pill"
              ></div>
            </div>
          </div>

          <div
            style={{ overflowY: "auto", overflow: "hidden" }}
            className="position-relative w-100 h-100"
          >
            {processingPreviewImage && (
              <div
                style={{ zIndex: 3 }}
                className="w-100 h-100 position-absolute d-flex justify-content-center align-items-center"
              >
                <span
                  style={{ zIndex: 3 }}
                  className="spinner-border spinner-border-lg"
                  role="status"
                  aria-hidden="true"
                ></span>
              </div>
            )}

            <canvas
              style={{
                maxHeight: "450px",
                objectFit: "scale-down",
              }}
              ref={canvasRef}
              className="w-100"
            />
          </div>

          <div className="w-100 d-flex justify-content-start">
            <i
              style={{ fontSize: 25 }}
              className="fa-light fa-heart text-muted"
            ></i>
          </div>
        </div>
      </div>{" "}
    </div>
  );
};

export default PostPreview;
